import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Award, Loader2 } from 'lucide-react';
import { usePreferences } from '../context/PreferencesContext';
import { useModuleAccess } from '../hooks/useModuleAccess';
import Layout from '../components/layout/Layout';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const GradePage = () => {
  const navigate = useNavigate();
  const { updatePreferences } = usePreferences();
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Module access hook for enforcement
  const { 
    isGradeActive, 
    getOralStatus, 
    getWrittenStatus,
    loading: moduleLoading 
  } = useModuleAccess();

  useEffect(() => {
    fetchGrades();
  }, []);

  const fetchGrades = async () => {
    try {
      const response = await axios.get(`${API}/grades/public`);
      setGrades(response.data.grades || []);
    } catch (error) {
      console.error('Failed to fetch grades:', error);
      // Fallback to static grades if API fails
      setGrades([
        { grade_key: 'MEO Class 1', display_name: 'MEO Class 1', status: 'active', category: 'Engineering' },
        { grade_key: 'MEO Class 2', display_name: 'MEO Class 2', status: 'active', category: 'Engineering' },
        { grade_key: 'MEO Class 4', display_name: 'MEO Class 4', status: 'active', category: 'Engineering' },
        { grade_key: 'ETO', display_name: 'ETO', status: 'coming_soon', category: 'Engineering' },
        { grade_key: 'Master', display_name: 'Master', status: 'coming_soon', category: 'Deck' },
        { grade_key: 'Chief Mate', display_name: 'Chief Mate', status: 'coming_soon', category: 'Deck' },
        { grade_key: '2MFG', display_name: '2MFG', status: 'coming_soon', category: 'Deck' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Get effective status based on module settings
  const getGradeStatus = (grade) => {
    const gradeName = grade.grade_key || grade.display_name;
    const oralStatus = getOralStatus(gradeName);
    const writtenStatus = getWrittenStatus(gradeName);
    
    // Grade is available if at least one exam type is active
    if (oralStatus === 'active' || writtenStatus === 'active') {
      return 'active';
    }
    // Grade is coming soon if at least one exam type is coming soon
    if (oralStatus === 'coming_soon' || writtenStatus === 'coming_soon') {
      return 'coming_soon';
    }
    // Otherwise disabled
    return 'disabled';
  };

  const handleGradeSelect = async (grade) => {
    const effectiveStatus = getGradeStatus(grade);
    
    if (effectiveStatus === 'disabled') {
      return; // Completely disabled grades shouldn't be clickable
    }
    
    if (effectiveStatus === 'coming_soon') {
      toast.info(`${grade.display_name} coming soon!`);
      return;
    }
    
    // Auto-set exam_type to 'orals' (Phase 5: Onboarding cleanup - removed exam type selection)
    await updatePreferences({ grade: grade.grade_key, exam_type: 'orals' });
    navigate('/mmd');
  };

  const engineeringGrades = grades.filter(g => g.category === 'Engineering');
  const deckGrades = grades.filter(g => g.category === 'Deck');

  // Render status badge for a grade
  const renderStatusBadge = (grade) => {
    const status = getGradeStatus(grade);
    const oralStatus = getOralStatus(grade.grade_key);
    const writtenStatus = getWrittenStatus(grade.grade_key);
    
    if (status === 'active') {
      // Show which exam types are available
      return (
        <div className="flex flex-wrap gap-1 justify-center">
          {oralStatus === 'active' && (
            <Badge className="bg-green-100 text-green-800 text-xs">Oral</Badge>
          )}
          {writtenStatus === 'active' && (
            <Badge className="bg-blue-100 text-blue-800 text-xs">Written</Badge>
          )}
          {oralStatus === 'coming_soon' && (
            <Badge variant="outline" className="text-xs text-yellow-700 border-yellow-400">Oral Soon</Badge>
          )}
          {writtenStatus === 'coming_soon' && (
            <Badge variant="outline" className="text-xs text-yellow-700 border-yellow-400">Written Soon</Badge>
          )}
        </div>
      );
    }
    
    if (status === 'coming_soon') {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 text-xs">Coming Soon</Badge>
      );
    }
    
    return null; // Disabled grades won't show
  };

  if (loading || moduleLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#0A7EA4]" />
        </div>
      </Layout>
    );
  }

  // Filter out completely disabled grades
  const visibleEngineeringGrades = engineeringGrades.filter(g => getGradeStatus(g) !== 'disabled');
  const visibleDeckGrades = deckGrades.filter(g => getGradeStatus(g) !== 'disabled');

  return (
    <Layout>
      <div className="page-transition">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#0A7EA4] mb-4">
            Select Your Grade
          </h1>
          <p className="text-gray-600 text-base">
            Choose the certification grade you&apos;re preparing for
          </p>
        </div>

        {/* Engineering Section */}
        {visibleEngineeringGrades.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Engineering</h2>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
              {visibleEngineeringGrades.map((grade) => {
                const status = getGradeStatus(grade);
                const isAvailable = status === 'active';
                const isComingSoon = status === 'coming_soon';
                
                return (
                  <Card
                    key={grade.grade_key}
                    data-testid={`grade-${grade.grade_key.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleGradeSelect(grade)}
                    className={`cursor-pointer hover-lift border-2 transition-colors ${
                      isAvailable
                        ? 'border-gray-200 hover:border-[#0A7EA4]'
                        : 'border-gray-200 opacity-60'
                    }`}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 bg-[#0A7EA4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Award className="w-8 h-8 text-[#0A7EA4]" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{grade.display_name}</h3>
                      {grade.description && (
                        <p className="text-sm text-gray-500 mb-2">{grade.description}</p>
                      )}
                      {renderStatusBadge(grade)}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Deck Section */}
        {visibleDeckGrades.length > 0 && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-800 mb-6">Deck</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {visibleDeckGrades.map((grade) => {
                const status = getGradeStatus(grade);
                const isAvailable = status === 'active';
                const isComingSoon = status === 'coming_soon';
                
                return (
                  <Card
                    key={grade.grade_key}
                    data-testid={`grade-${grade.grade_key.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => handleGradeSelect(grade)}
                    className={`cursor-pointer hover-lift border-2 transition-colors ${
                      isAvailable
                        ? 'border-gray-200 hover:border-[#14B8A6]'
                        : 'border-gray-200 opacity-60'
                    }`}
                  >
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 bg-[#14B8A6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Award className="w-8 h-8 text-[#14B8A6]" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{grade.display_name}</h3>
                      {grade.description && (
                        <p className="text-sm text-gray-500 mb-2">{grade.description}</p>
                      )}
                      {renderStatusBadge(grade)}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Show message if no grades available */}
        {visibleEngineeringGrades.length === 0 && visibleDeckGrades.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No grades are currently available. Please check back later.</p>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default GradePage;
